import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Security, Home } from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';
import AdminLogin from './AdminLogin';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermission 
}) => {
  const { admin, isAuthenticated, checkPermission } = useAdminAuth();

  // Show login if not authenticated
  if (!isAuthenticated || !admin) {
    return <AdminLogin />;
  }

  // Check specific permission if required
  if (requiredPermission && !checkPermission(requiredPermission)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 500,
            borderRadius: 3,
          }}
        >
          <Security sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Access Denied
          </Typography>
          
          <Typography variant="body1" color="text.secondary" mb={3}>
            You don't have permission to access this section. Please contact your administrator if you believe this is an error.
          </Typography>

          <Box mb={3}>
            <Typography variant="body2" color="text.secondary">
              <strong>Your Role:</strong> {admin.role.replace('_', ' ').toUpperCase()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Required Permission:</strong> {requiredPermission}
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={() => window.location.href = '/'}
            sx={{ mr: 2 }}
          >
            Return to Dashboard
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  // User has access, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;